<template>
  <input
    ref="inputRef"
    :id="id"
    :type="inputType"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :class="inputClasses"
    :value="displayValue"
    @input="handleInput"
    @focus="handleFocus"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @paste="handlePaste"
    v-bind="$attrs"
  />
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue'

export default {
  name: 'UIInputMask',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, null],
      default: null
    },
    mask: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    slotChar: {
      type: String,
      default: '_'
    },
    autoClear: {
      type: Boolean,
      default: true
    },
    unmask: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: null
    },
    inputType: {
      type: String,
      default: 'text'
    }
  },
  emits: ['update:modelValue', 'focus', 'blur', 'keydown', 'complete', 'keypress', 'paste'],
  setup(props, { emit, attrs }) {
    const inputRef = ref(null)
    const displayValue = ref(props.modelValue ? String(props.modelValue) : '')
    const isCompleted = ref(false)
    const caretPosition = ref(0)

    // Parse mask into pattern
    const maskPattern = computed(() => {
      if (!props.mask) return null
      
      const pattern = []
      const maskChars = props.mask.split('')
      
      for (let i = 0; i < maskChars.length; i++) {
        const char = maskChars[i]
        if (char === '9') {
          pattern.push({ type: 'digit', char: '9' })
        } else if (char === 'a') {
          pattern.push({ type: 'alpha', char: 'a' })
        } else if (char === '*') {
          pattern.push({ type: 'alphanum', char: '*' })
        } else {
          pattern.push({ type: 'literal', char: char })
        }
      }
      
      return pattern
    })

    // Get mask length
    const maskLength = computed(() => {
      return props.mask ? props.mask.length : 0
    })

    // CSS classes
    const inputClasses = computed(() => {
      const baseClasses = [
        'w-full',
        'px-4',
        'py-2',
        'border',
        'rounded-md',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-blue-500',
        'focus:border-blue-500',
        'transition-colors',
        'duration-200'
      ]

      // Add state-specific classes
      if (props.invalid) {
        baseClasses.push('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      } else {
        baseClasses.push('border-gray-300')
      }

      if (props.disabled) {
        baseClasses.push('bg-gray-100', 'cursor-not-allowed', 'opacity-60')
      } else if (props.readonly) {
        baseClasses.push('bg-gray-50', 'cursor-default')
      } else {
        baseClasses.push('bg-white')
      }

      // Merge with any classes from attrs
      if (attrs.class) {
        if (typeof attrs.class === 'string') {
          baseClasses.push(...attrs.class.split(' '))
        } else if (Array.isArray(attrs.class)) {
          baseClasses.push(...attrs.class)
        }
      }

      return baseClasses.filter(Boolean).join(' ')
    })

    // Validate character against mask pattern
    const isValidChar = (char, patternIndex) => {
      if (!maskPattern.value || patternIndex >= maskPattern.value.length) {
        return false
      }

      const pattern = maskPattern.value[patternIndex]
      
      switch (pattern.type) {
        case 'digit':
          return /\d/.test(char)
        case 'alpha':
          return /[a-zA-Z]/.test(char)
        case 'alphanum':
          return /[a-zA-Z0-9]/.test(char)
        default:
          return false
      }
    }

    // Get next input position (skip literals)
    const getNextInputPosition = (pos) => {
      if (!maskPattern.value || !maskPattern.value.length) return pos
      
      while (pos < maskPattern.value.length && maskPattern.value[pos].type === 'literal') {
        pos++
      }
      return pos
    }

    // Get previous input position (skip literals)
    const getPrevInputPosition = (pos) => {
      if (!maskPattern.value || !maskPattern.value.length) return pos
      
      pos--
      while (pos >= 0 && maskPattern.value[pos].type === 'literal') {
        pos--
      }
      return Math.max(0, pos)
    }

    // Format value according to mask
    const formatValue = (value) => {
      if (!props.mask || !value) return value || ''
      
      const chars = value.replace(/\D/g, '').split('')
      let formatted = ''
      let charIndex = 0
      
      for (let i = 0; i < maskPattern.value.length && charIndex < chars.length; i++) {
        const pattern = maskPattern.value[i]
        
        if (pattern.type === 'literal') {
          formatted += pattern.char
        } else if (isValidChar(chars[charIndex], i)) {
          formatted += chars[charIndex]
          charIndex++
        } else {
          break
        }
      }
      
      return formatted
    }

    // Get raw value (without mask literals)
    const getRawValue = (value) => {
      if (!props.mask || !value) return value || ''
      
      if (props.unmask) {
        return value.replace(/[^\w]/g, '')
      }
      
      return value
    }

    // Check if mask is complete
    const isMaskComplete = (value) => {
      if (!props.mask || !value) return false
      
      const rawLength = value.replace(/[^\w]/g, '').length
      const requiredLength = maskPattern.value?.filter(p => p.type !== 'literal').length || 0
      
      return rawLength === requiredLength
    }

    // Handle input event
    const handleInput = (event) => {
      const inputValue = event.target.value
      const cursorPos = event.target.selectionStart
      
      if (props.readonly || props.disabled) return
      
      const formatted = formatValue(inputValue)
      displayValue.value = formatted
      
      // Update model value
      const outputValue = getRawValue(formatted)
      emit('update:modelValue', outputValue)
      
      // Check completion
      const completed = isMaskComplete(formatted)
      if (completed && !isCompleted.value) {
        isCompleted.value = true
        emit('complete', { value: outputValue, formatted })
      } else if (!completed && isCompleted.value) {
        isCompleted.value = false
      }
      
      // Set cursor position
      nextTick(() => {
        if (inputRef.value) {
          const newPos = getNextInputPosition(cursorPos)
          inputRef.value.setSelectionRange(newPos, newPos)
        }
      })
    }

    // Handle focus event
    const handleFocus = (event) => {
      if (props.readonly || props.disabled) return
      
      emit('focus', event)
      
      // Position cursor at first input position
      nextTick(() => {
        if (inputRef.value) {
          const firstInputPos = getNextInputPosition(0)
          inputRef.value.setSelectionRange(firstInputPos, firstInputPos)
        }
      })
    }

    // Handle blur event
    const handleBlur = (event) => {
      emit('blur', event)
      
      // Auto-clear if enabled and incomplete
      if (props.autoClear && displayValue.value && !isMaskComplete(displayValue.value)) {
        displayValue.value = ''
        emit('update:modelValue', '')
      }
    }

    // Handle keydown event
    const handleKeydown = (event) => {
      emit('keydown', event)
      
      if (props.readonly || props.disabled) return
      
      const { key, target } = event
      const cursorPos = target.selectionStart
      
      // Ensure displayValue is defined and has a value
      if (!displayValue.value) {
        displayValue.value = ''
      }
      
      // Handle backspace
      if (key === 'Backspace') {
        event.preventDefault()
        
        if (cursorPos > 0) {
          const prevPos = getPrevInputPosition(cursorPos)
          const currentValue = String(displayValue.value || '')
          const newValue = currentValue.substring(0, prevPos) + 
                          currentValue.substring(cursorPos)
          
          displayValue.value = formatValue(newValue) || ''
          emit('update:modelValue', getRawValue(displayValue.value))
          
          nextTick(() => {
            if (inputRef.value) {
              inputRef.value.setSelectionRange(prevPos, prevPos)
            }
          })
        }
      }
      
      // Handle delete
      else if (key === 'Delete') {
        event.preventDefault()
        
        const currentValue = String(displayValue.value || '')
        if (cursorPos < currentValue.length) {
          const nextPos = getNextInputPosition(cursorPos)
          const newValue = currentValue.substring(0, cursorPos) + 
                          currentValue.substring(nextPos + 1)
          
          displayValue.value = formatValue(newValue) || ''
          emit('update:modelValue', getRawValue(displayValue.value))
          
          nextTick(() => {
            if (inputRef.value) {
              inputRef.value.setSelectionRange(cursorPos, cursorPos)
            }
          })
        }
      }
      
      // Handle arrow keys
      else if (key === 'ArrowLeft') {
        event.preventDefault()
        const newPos = getPrevInputPosition(cursorPos)
        inputRef.value.setSelectionRange(newPos, newPos)
      }
      
      else if (key === 'ArrowRight') {
        event.preventDefault()
        const newPos = getNextInputPosition(cursorPos + 1)
        inputRef.value.setSelectionRange(newPos, newPos)
      }
      
      // Handle printable characters
      else if (key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault()
        
        const nextInputPos = getNextInputPosition(cursorPos)
        if (props.mask && maskLength.value != null && nextInputPos < maskLength.value && isValidChar(key, nextInputPos)) {
          const currentValue = String(displayValue.value || '')
          const newValue = currentValue.substring(0, nextInputPos) + 
                          key + 
                          currentValue.substring(nextInputPos + 1)
          
          displayValue.value = formatValue(newValue) || ''
          emit('update:modelValue', getRawValue(displayValue.value))
          
          nextTick(() => {
            if (inputRef.value) {
              const nextPos = getNextInputPosition(nextInputPos + 1)
              inputRef.value.setSelectionRange(nextPos, nextPos)
            }
          })
        }
      }
    }

    // Handle paste event
    const handlePaste = (event) => {
      emit('paste', event)
      
      if (props.readonly || props.disabled) return
      
      event.preventDefault()
      
      const pastedData = event.clipboardData.getData('text')
      const formatted = formatValue(pastedData)
      
      displayValue.value = formatted
      emit('update:modelValue', getRawValue(formatted))
      
      // Check completion
      const completed = isMaskComplete(formatted)
      if (completed) {
        isCompleted.value = true
        emit('complete', { value: getRawValue(formatted), formatted })
      }
    }

    // Watch for external model value changes
    watch(() => props.modelValue, (newValue) => {
      const formatted = formatValue(newValue)
      if (formatted !== getRawValue(displayValue.value)) {
        displayValue.value = formatted || ''
      }
    }, { immediate: true })

    // Initialize display value
    onMounted(() => {
      displayValue.value = formatValue(props.modelValue) || ''
    })

    return {
      inputRef,
      displayValue,
      inputClasses,
      handleInput,
      handleFocus,
      handleBlur,
      handleKeydown,
      handlePaste
    }
  }
}
</script>

<style scoped>
/* Component-specific styles if needed */
</style> 